import React from "react";
import { Box } from "@mui/material";
import { StriimMenuList, StriimMenuItem } from "@striim/striim-ui-v2";
import Tracker from "core/services/tracker/tracker";
import { TRACKER_STRINGS } from "core/services/tracker/constants";
import { useAppSelector } from "src/stores/redux/hooks";
import { getCurrentLayout, getCurrentPlacementAlgorithm } from "../helpers/selectors";
import { PlacementAlgorithm } from "src/stores/redux/features/flowdesigner/flowdesignerslice.types";
import { useFlowLayout } from "../helpers/flow.hooks";
import { Layout, Layouts } from "../helpers/flow.constants";
import LayoutDirectionSvg from "src/generic/icon/customIcons/layoutDirection.svg";
import GridIconSvg from "app/images/striimline/grid.svg";

const LayoutDirection = (LayoutDirectionSvg as unknown) as React.FC<React.SVGProps<SVGSVGElement>>;
const GridIcon = (GridIconSvg as unknown) as React.FC<React.SVGProps<SVGSVGElement>>;

const ALGORITHM_LABELS = {
    CLASSIC: "Classic",
    ENHANCED_HORIZONTAL: "Enhanced (Horizontal)",
    ENHANCED_VERTICAL: "Enhanced (Vertical)"
};

const styles = {
    iconWrapper: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: 1
    },
    verticalIcon: { transform: "rotate(90deg)" }
};

export const getLayoutInfo = (
    algorithm: PlacementAlgorithm,
    direction: Layout
): { label: string; icon: React.ReactElement | null } => {
    if (algorithm === "sugiyama") {
        return { label: ALGORITHM_LABELS.CLASSIC, icon: <GridIcon style={{ width: "20px", height: "20px" }} /> };
    }
    if (direction === Layouts.HORIZONTAL) {
        return {
            label: ALGORITHM_LABELS.ENHANCED_HORIZONTAL,
            icon: <LayoutDirection style={{ width: "16px", height: "16px" }} />
        };
    }
    return {
        label: ALGORITHM_LABELS.ENHANCED_VERTICAL,
        icon: (
            <LayoutDirection
                style={{
                    ...styles.verticalIcon,
                    ...{ width: "16px", height: "16px" }
                }}
            />
        )
    };
};

const ArrangementsDropdown = () => {
    const { onArrangementChange } = useFlowLayout();
    const currentPlacementAlgorithm = useAppSelector(getCurrentPlacementAlgorithm);
    const currentLayout = useAppSelector(getCurrentLayout);

    const handlePlacementAlgorithmChange = (algorithm: PlacementAlgorithm, direction: Layout) => {
        const { label } = getLayoutInfo(algorithm, direction);
        Tracker.getInstance().track(TRACKER_STRINGS.schema.flowDesigner, {
            event: TRACKER_STRINGS.eventClicked.flowDesigner,
            buttonClicked: "Arrangement",
            layout: label
        });
        onArrangementChange(algorithm, direction);
    };

    const isClassic = currentPlacementAlgorithm === "sugiyama";
    const isEnhancedHorizontal = currentPlacementAlgorithm === "dagre" && currentLayout === Layouts.HORIZONTAL;
    const isEnhancedVertical = currentPlacementAlgorithm === "dagre" && currentLayout === Layouts.VERTICAL;

    const classic = getLayoutInfo("sugiyama", Layouts.VERTICAL);
    const enhancedVertical = getLayoutInfo("dagre", Layouts.VERTICAL);
    const enhancedHorizontal = getLayoutInfo("dagre", Layouts.HORIZONTAL);

    return (
        <StriimMenuList>
            <StriimMenuItem
                onClick={() => handlePlacementAlgorithmChange("sugiyama", Layouts.VERTICAL)}
                selected={isClassic}
                data-test-id="arrange-classic"
            >
                <Box sx={styles.iconWrapper}>{classic.icon}</Box>
                {classic.label}
            </StriimMenuItem>
            <StriimMenuItem
                onClick={() => handlePlacementAlgorithmChange("dagre", Layouts.VERTICAL)}
                selected={isEnhancedVertical}
                data-test-id="arrange-vertical"
            >
                <Box sx={styles.iconWrapper}>{enhancedVertical.icon}</Box>
                {enhancedVertical.label}
            </StriimMenuItem>
            <StriimMenuItem
                onClick={() => handlePlacementAlgorithmChange("dagre", Layouts.HORIZONTAL)}
                selected={isEnhancedHorizontal}
                data-test-id="arrange-horizontal"
            >
                <Box sx={styles.iconWrapper}>{enhancedHorizontal.icon}</Box>
                {enhancedHorizontal.label}
            </StriimMenuItem>
        </StriimMenuList>
    );
};

export default ArrangementsDropdown;
